/**
 * PresetShell — chooses the chrome (layout + nav) based on the effective preset.
 *
 * v2 (Nocturne): The preset registry can now provide two shells per preset:
 *   - shellId         → "main" shell for logged-in / primary routes
 *   - authShellId     → optional shell for auth / rules / landing / static pages
 *
 * Preset 1 does NOT declare an auth shell, so its auth + rules + landing
 * + terms + privacy routes render bare exactly like before (byte-safe).
 */
import { useLocation } from "react-router-dom";
import { usePreset } from "../context/PresetProvider";
import { resolveShell, resolveAuthShell, presetSupportsAuthShell } from "../registry";

// Paths that should never show any preset shell chrome (admin, support, public checkout, multi-vendor).
const NEVER_SHELL = [
    /^\/admin(\/|$)/,
    /^\/support(\/|$)/,
    /^\/plans(\/|$)/,
    /^\/c\//,
    /^\/vendor(\/|$)/,
    /^\/v\//,
];

// Paths that are AUTH-SHELL candidates — rendered inside the preset's auth shell
// ONLY if the preset declares one; otherwise rendered bare.
const AUTH_SHELL_PATHS = [
    /^\/auth(\/|$)/,
    /^\/landing$/,
    /^\/terms$/,
    /^\/privacy$/,
    /^\/rules(\/|$)/,
];

function matches(patterns, pathname) {
    return patterns.some((re) => re.test(pathname));
}

export default function PresetShell({ children }) {
    const { preset } = usePreset();
    const location = useLocation();
    const pathname = location.pathname || "/";

    // 1) Never show any preset chrome for admin / support / public checkout
    if (matches(NEVER_SHELL, pathname)) {
        return <>{children}</>;
    }

    // 2) Auth / rules / static pages
    if (matches(AUTH_SHELL_PATHS, pathname)) {
        if (presetSupportsAuthShell(preset?.id)) {
            const AuthShell = resolveAuthShell(preset?.id);
            return <AuthShell preset={preset}>{children}</AuthShell>;
        }
        // Preset 1 behaviour: bare (byte-safe)
        return <>{children}</>;
    }

    // 3) Main shell
    const Shell = resolveShell(preset?.shell_id);
    return <Shell preset={preset}>{children}</Shell>;
}
